"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Database, HardDrive, MessageSquare, ShieldCheck, BarChart3, Layers3 } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const highlights = [{
  icon: Database,
  title: "Expand vectors",
  text: "Add more vector capacity without changing your whole subscription."
}, {
  icon: HardDrive,
  title: "Expand storage",
  text: "Increase indexed storage for larger approved knowledge bases."
}, {
  icon: MessageSquare,
  title: "Expand queries",
  text: "Support higher monthly usage when tenant demand increases."
}];
export default function PredictableScaling() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: visualRef,
    isVisible: isVisualVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section id="predictable-scaling" className="relative overflow-hidden bg-background py-14 lg:py-18">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/7 via-background to-background" />
      <div className="pointer-events-none absolute right-0 top-0 h-[340px] w-[340px] rounded-full bg-primary/5 blur-3xl" />
      <div className="pointer-events-none absolute left-0 bottom-0 h-[260px] w-[260px] rounded-full bg-primary/5 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-10">
          <div ref={headerRef} className={cn("lg:col-span-7 transition-all duration-700 ease-out", isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")}>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
              <Layers3 className="size-4" />
              <span>Capacity Packs</span>
            </div>

            <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-[3.45rem] leading-[1.02]">
              Scale capacity without changing your core plan
            </h1>

            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground md:text-[1.12rem]">
              Capacity Packs are monthly add-ons that expand storage, vectors, or query limits in a
              predictable way. They help teams and agencies grow usage without relying on unlimited plans
              or silent overages.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="h-12 px-8 text-base shadow-sm">
                <Link to="/PricingPage">
                  View Pricing
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="h-12 bg-background/60 px-8 text-base backdrop-blur-sm">
                <Link to="/BookADemoContactSalesPage">
                  Book a Demo
                </Link>
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm font-medium text-muted-foreground">
              <span>One resource type per pack</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>No unlimited usage</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>Hard caps stay enforced</span>
              <span className="hidden sm:inline text-border">•</span>
              <span>Predictable scaling</span>
            </div>
          </div>

          <div ref={visualRef} className={cn("lg:col-span-5 transition-all duration-1000 delay-150 ease-out", isVisualVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
            <Card className="rounded-2xl border border-border/70 bg-card/95 p-6 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.16)] backdrop-blur-xl">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider text-primary">
                    Scaling model
                  </div>
                  <div className="mt-1 text-xl font-semibold text-foreground">
                    Add only what you need
                  </div>
                </div>
                <div className="rounded-xl bg-primary/10 p-3">
                  <BarChart3 className="size-6 text-primary" />
                </div>
              </div>

              <div className="space-y-4">
                {highlights.map(item => {
                const Icon = item.icon;
                return <div key={item.title} className="rounded-xl border border-border/70 bg-secondary/25 p-4">
                      <div className="flex items-start gap-3">
                        <div className="rounded-lg bg-primary/10 p-2 shrink-0">
                          <Icon className="size-4 text-primary" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground">
                            {item.title}
                          </div>
                          <div className="mt-1 text-sm leading-relaxed text-muted-foreground">
                            {item.text}
                          </div>
                        </div>
                      </div>
                    </div>;
              })}
              </div>

              <div className="mt-5 rounded-xl border border-primary/20 bg-primary/[0.04] p-4">
                <div className="text-sm font-semibold text-foreground">
                  What this means
                </div>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  You do not need to jump plans every time usage grows. Capacity Packs let you expand
                  the specific resource that is becoming the bottleneck.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}